import {FC, PropsWithChildren, useEffect, useRef} from 'react';
import {debounceTime, Subject} from 'rxjs';

import {inAnimationFrameOrHiddenCallbackAsync} from '../../../libs/shared/core/src/helpers/async/inAnimationFrame';
import {TaskSourcesEnum} from '../../../libs/shared/core/src/helpers/instrumentation/eventLoopPerformanceHelpers';
import {useConstant} from '../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {useForceUpdate} from '../../../libs/shared/core/src/helpers/react/hooks/useForceUpdate';
import {
  buildCancelTokenSource,
  CancelTokenSource,
} from '../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {reconnectRuntimeFeatureFlagsAsync} from '../../../libs/shared/core/src/services/featureFlag/featureFlags';
import {
  buildPushContentRefreshCompleteMessage,
  PushSystemMessagesEnum,
} from '../../../libs/shared/core/src/services/push/common/pushMessage';
import {syncStashWithServerAsyncAction} from '../../../libs/shared/core/src/store/data/stash/stashThunkActions';
import {usePushControllerContext} from '../../../libs/shared/ui/src/push/pushControllerContext';
import {usePushV2SystemEvent} from '../../../libs/shared/ui/src/push/usePushEvents';
import {useAppDispatch} from '../../../src/helpers/appReduxHelpers';
import {refreshBootAction} from '../../../src/store/data/dataActions';
import {
  SyncCellsListsReasonsEnum,
  trySyncUserCellsListsAsyncAction,
} from '../../../src/store/metadata/cellsLists/cellsListsActions';

export type RawBootHandler = (rawBoot: unknown) => void;

export interface PushContentRefresherProps {
  onRawBoot?: RawBootHandler;
  shouldRefreshOnInit?: boolean;
}

export const PushContentRefresher: FC<PropsWithChildren<PushContentRefresherProps>> = ({
  children,
  onRawBoot,
  shouldRefreshOnInit = false,
}) => {
  const forceUpdate = useForceUpdate();
  const dispatch = useAppDispatch();
  const cancelTokenSourceRef = useRef<CancelTokenSource>();
  const requestContentRefreshSubject$ = useConstant(() => new Subject<void>());
  const {pushController} = usePushControllerContext();

  usePushV2SystemEvent((event) => {
    if (event.type !== PushSystemMessagesEnum.REQUEST_CONTENT_REFRESH) {
      return;
    }

    requestContentRefreshSubject$.next();
  });

  useEffect(() => {
    const subscription = requestContentRefreshSubject$.pipe(debounceTime(1000)).subscribe(() => {
      cancelTokenSourceRef.current?.cancel();
      const cancelTokenSource = buildCancelTokenSource();
      const cancelToken = cancelTokenSource.token;

      cancelTokenSourceRef.current = cancelTokenSource;

      dispatch(refreshBootAction({onRawBoot, cancelToken}));
      dispatch(trySyncUserCellsListsAsyncAction(SyncCellsListsReasonsEnum.RECONNECT, cancelToken));
      reconnectRuntimeFeatureFlagsAsync();
      dispatch(syncStashWithServerAsyncAction(cancelToken));
      inAnimationFrameOrHiddenCallbackAsync(
        () => {
          forceUpdate();
          pushController.dispatchSystemEvent(buildPushContentRefreshCompleteMessage());
        },
        {source: TaskSourcesEnum.PUSH},
      );
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [dispatch, forceUpdate, onRawBoot, pushController, requestContentRefreshSubject$]);

  useEffect(() => {
    if (shouldRefreshOnInit) {
      requestContentRefreshSubject$.next();
    }
  }, [requestContentRefreshSubject$, shouldRefreshOnInit]);

  return children;
};
